(ns common.schema.entities.survey.survey-profile
  (:require
   [common.schema.entities.survey.survey-auth-type :as survey-auth-type]
   [common.schema.entities.survey.survey-cc-type :as survey-cc-type]
   [common.schema.entities.survey.survey-profile-status :as survey-profile-status]
   [common.schema.entities.survey.survey-sharing-type :as survey-sharing-type]
   [common.schema.option-filters :as option-filters]
   [common.schema.schema-utils :as su]))

(def schema
  (vec
   (concat
    (su/text-simple-field :survey-profile/name
                          "Name"
                          {:composite-attributes     [:survey-profile/target]
                           :calculation-triggers     {:entity.type/holding  [[:survey-profile/_target]]
                                                      :entity.type/investor [[:survey-profile/_target]]
                                                      :entity.type/person   [[:survey-profile/_target]]
                                                      :entity.type/company  [[:survey-profile/_target]]}
                           :name-attribute           :survey-profile/name
                           :editor-type              :editor.type/none
                           :default-field-visibility :in-use-disabled})
    (su/single-ref-field :survey-profile/type
                         "Type"
                         [:entity.type/survey-type]
                         {:mandatory-config         :mandatory/always
                          :default-field-visibility :in-use-disabled
                          :system-required?         true
                          :system-semantics
                          su/user-provided-system-required-field-semantics})
    (su/single-ref-field :survey-profile/target
                         "Target"
                         [:entity.type/any]
                         {:mandatory-config         :mandatory/always
                          :default-field-visibility :in-use-disabled
                          :system-required?         true
                          :system-semantics
                          su/user-provided-system-required-field-semantics
                          :create-on-fly?           true})
    (su/keyword-field :survey-profile/status "Status" survey-profile-status/options {:default-field-visibility :in-use-disabled})
    (su/calculated-field :survey-profile/last-process-status "Last process status"
                         {:default-field-visibility :in-use-disabled
                          :data-type                :data.type/keyword
                          :calculation-triggers     {:entity.type/survey-process [[:survey-profile/_survey-processes]]}
                          :calc-fn                  (fn [{:keys [entity-data]}]
                                                      (:survey-profile/last-process-status entity-data))})
    (su/calculated-field :survey-profile/last-process-invitees "Last process invitees"
                         {:default-field-visibility  :in-use-disabled
                          :data-type                 :data.type/text-simple
                          :calculation-triggers     {:entity.type/survey-process [[:survey-profile/_survey-processes]]}
                          :calc-fn                   (fn [{:keys [entity-data]}]
                                                       (:survey-profile/last-process-invitees entity-data))})
    (su/single-ref-field :survey-profile/our-contact
                         "Our contact"
                         [:entity.type/account]
                         {:option-filter-fn         option-filters/active-users
                          :os-filter-terms-fn       (fn [{:keys [] :as args}]
                                                      {"extras.active-user" true})
                          :default-field-visibility :in-use-disabled})
    (su/single-ref-field :survey-profile/their-contact
                         "Their contact"
                         [:entity.type/person]
                         {:default-field-visibility :in-use-disabled})
    (su/single-ref-field :survey-profile/primary-manager
                         "Our manager"
                         [:entity.type/account]
                         {:default-field-visibility :in-use-disabled})
    (su/multi-ref-field :survey-profile/other-managers
                        "Other managers"
                        [:entity.type/account]
                        {:default-field-visibility :in-use-disabled})
    (su/text-multiline-field :survey-profile/notes
                             "Notes"
                             {:default-field-visibility :in-use})
    (su/multi-ref-field :survey-profile/survey-processes
                        "Survey processes"
                        [:entity.type/survey-process]
                        {:editor-type              :editor.type/none
                         :grid-type                :editor.type/none
                         :filter-type              :filter.type/none
                         :default-field-visibility :in-use-disabled})
    (su/multi-ref-field :survey-profile/rotation-period-overrides
                        "Rotation period overrides"
                        [:entity.type/rotation-period-override]
                        {:editor-type              :editor.type/none
                         :grid-type                :editor.type/none
                         :filter-type              :filter.type/none
                         :default-field-visibility :in-use-disabled})

    ;; What every send to this target should use. A survey process takes a
    ;; copy of these when its batch is created and then lives its own life, so
    ;; editing the profile never changes a survey already with a respondent.
    ;;
    ;; Recipients are the profile's own list rather than
    ;; `last-process-invitees`, which is a calculated mirror of whichever
    ;; process happened to be latest - the reason a profile could be sent to
    ;; with nobody on it.
    ;;
    ;; People rather than stored addresses, matching `their-contact` just
    ;; above: one copy of a contact's details, a picker instead of free text,
    ;; and a recipient whose address changes stays correct. The process burns
    ;; them down to {:email :phone :survey-invite-token} when its batch is
    ;; created, so a survey already with a respondent is untouched by later
    ;; edits here.
    ;;
    ;; Note what a ref cannot promise that an address could: a person may have
    ;; no email. The mass-send preview has to say so rather than silently
    ;; sending to nobody.
    (su/multi-ref-field :survey-profile/recipients
                        "Recipients"
                        [:entity.type/person]
                        {:create-entity-types      [:entity.type/person]
                         :authorization            su/access-user-user
                         :default-field-visibility :in-use})
    (su/keyword-field :survey-profile/default-auth-type
                      "Default authentication"
                      survey-auth-type/options
                      {:authorization            su/access-user-user
                       :default-field-visibility :in-use})
    ;; Disabled until the re-sharing endpoint exists. The attribute is stored
    ;; from the start so the migration is not repeated, but an editable field
    ;; would promise an enforcement that nothing performs yet.
    (su/keyword-field :survey-profile/default-sharing-type
                      "Default re-sharing"
                      survey-sharing-type/options
                      {:authorization            su/access-user-user
                       :default-field-visibility :in-use-disabled})
    ;; Only read when the re-sharing default is :sharing-type/allowed-domains.
    ;; Disabled for the same reason as the field above.
    (su/text-edn-field :survey-profile/sharing-allowed-domains
                       "Allowed re-sharing domains"
                       {:authorization            su/access-user-user
                        :default-field-visibility :in-use-disabled})
    ;; Who is copied, chosen from the people the profile already names rather
    ;; than by listing them again. See `survey-cc-type`.
    (su/keyword-field :survey-profile/cc-type
                      "Copy invitations to"
                      survey-cc-type/options
                      {:authorization            su/access-user-user
                       :default-field-visibility :in-use})
    ;; Anyone the manager fields do not reach. Refs rather than addresses, so
    ;; the list follows people rather than going stale when someone leaves.
    ;; Only read when `cc-type` includes it.
    (su/multi-ref-field :survey-profile/other-cc-recipients
                        "Other CC recipients"
                        [:entity.type/account]
                        {:option-filter-fn         option-filters/active-users
                         :os-filter-terms-fn       (fn [_args]
                                                     {"extras.active-user" true})
                         :authorization            su/access-user-user
                         :default-field-visibility :in-use}))))
